import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";

import StripePaymentElement from "./StripePaymentElement";
import { showToast } from "../../utils/helper";
import { trackEvent } from "../../utils/analytics";
import { API } from "../../utils/api";

const ExecutePayment = ({
  show,
  paymentInfo,
  bookingInfo,
  onClose,
  resetForm,
  onSuccess,
}) => {
  const [failureDialog, setFailureDialog] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const recordFailure = (errorMessage) =>
    API.failedBookingPayment({
      paymentId: paymentInfo.paymentId,
      bookingId: bookingInfo.id,
      errorMessage,
    }).catch(() => {});

  const handlePaid = () => {
    if (confirming) return;
    setConfirming(true);

    API.confirmPayment({
      paymentId: paymentInfo.paymentId,
      bookingId: bookingInfo.id,
    })
      .then((res) => {
        if (res && res.type === "success") {
          trackEvent("web_payment_success", {
            booking_id: bookingInfo && bookingInfo.id,
            amount: paymentInfo && paymentInfo.amount,
          });
          showToast(
            "success",
            "Payment Successful : Your booking has been confirmed!",
          );
          setTimeout(() => {
            if (onSuccess) onSuccess();
          }, 1500);
        } else {
          trackEvent("web_payment_confirm_failed", {
            booking_id: bookingInfo && bookingInfo.id,
          });
          showToast(
            "error",
            (res && res.message) ||
              "Payment succeeded but we couldn't update your booking. Please contact support.",
          );
          setConfirming(false);
        }
      })
      .catch((err) => {
        trackEvent("web_payment_confirm_failed", {
          booking_id: bookingInfo && bookingInfo.id,
        });
        showToast(
          "error",
          (err && err.message) ||
            "Payment succeeded but we couldn't update your booking. Please contact support.",
        );
        setConfirming(false);
      });
  };

  // `goToDialog` true -> show the retry/cancel dialog (non-recoverable error);
  // false -> just a toast, keep the form open for the user to correct input.
  const handleFailed = (message, goToDialog) => {
    trackEvent("web_payment_failed", {
      booking_id: bookingInfo && bookingInfo.id,
      recoverable: !goToDialog,
    });
    if (goToDialog) {
      recordFailure(message || "Payment failed");
      setFailureDialog(true);
    } else {
      showToast("error", message || "Payment failed");
    }
  };

  const handleCancelClicked = () => {
    recordFailure("Payment is canceled by user").finally(() => {
      showToast("error", "Payment is canceled by you");
      resetForm();
      onClose();
    });
  };

  const handleCancelBookingFromDialog = () => {
    setFailureDialog(false);
    resetForm();
    onClose();
  };

  const handleTryAgain = () => setFailureDialog(false);

  return (
    <Modal
      show={show}
      onHide={() => onClose()}
      className="booking-payment-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Booking Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {confirming && (
          <p className="text-center my-4">
            <i className="fas fa-spin fa-spinner" style={{ fontSize: 26 }} />
            <span className="d-block mt-2 text-muted">
              Confirming your booking…
            </span>
          </p>
        )}

        {!confirming && failureDialog && (
          <div className="payment-failure-dialog">
            <h5 className="fw-bold mb-2">Payment Failed</h5>
            <p className="mb-4">
              We were unable to process your payment. Please try again or use a
              different payment method.
            </p>
            <div className="d-grid gap-2">
              <Button
                variant=""
                className="btn-pill-primary"
                onClick={handleTryAgain}
              >
                <span>Try Again</span>
              </Button>
              <Button
                variant=""
                className="btn-pill-outline"
                onClick={handleCancelBookingFromDialog}
              >
                <span>Cancel Booking</span>
              </Button>
            </div>
          </div>
        )}

        {!confirming && !failureDialog && (
          <StripePaymentElement
            paymentInfo={paymentInfo}
            bookingId={bookingInfo && bookingInfo.id}
            onPaid={handlePaid}
            onFailed={handleFailed}
            onCancel={handleCancelClicked}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ExecutePayment;
